import React, { useEffect, useState } from 'react';
import { Card, Button, message, Modal, Typography } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { createCheckoutSession, getCurrentSubscription, unsubscribe } from '../../api/subscription';

const { Title, Paragraph, Text } = Typography;

const PricingSection = ({ pricingPlans }) => {
    const [loadingPriceId, setLoadingPriceId] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentSubscription, setCurrentSubscription] = useState(null);

    useEffect(() => {
        async function fetchCurrentSubscription() {
            try {
                const data = await getCurrentSubscription();
                setCurrentSubscription(data.subscription);
                console.log('Current subscription:', data.subscription);
            } catch (error) {
                console.error('Error fetching current subscription:', error);
            }
        }
        fetchCurrentSubscription();
    }, [])

    const handleChoosePlan = async (priceId) => {
        try {
            setLoadingPriceId(priceId);
            try {
                const response = await createCheckoutSession(priceId);
                console.log('Checkout session created:', response);
                // Redirect to Stripe checkout
                window.location.href = response.checkoutUrl;
            }
            catch (e) {
                throw new Error('Failed to create checkout session', e);
            }
        } catch (error) {
            console.error('Checkout error:', error);
            message.error('Failed to initiate checkout process. Please try again.');
        } finally {
            setLoadingPriceId(null);
        }
    };

    const showConfirmModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const confirmUnsubscribe = async () => {
        try {
            const response = await unsubscribe();
            message.success('Unsubscribed successfully');
            // Log the user out
            localStorage.removeItem("token");

            // Redirect to the unsubscribe success page
            window.location.href = "/unsubscribed";
        }
        catch (e) {
            console.error('Failed to unsubscribe:', e);
            message.error('Failed to unsubscribe. Please contact admin.');
        }
    };

    return (
        <Card
            title="Pricing"
            className="w-full"
        >
            <div className="grid md:grid-cols-3 gap-8 px-4 py-2">
                {pricingPlans.map((plan) => plan.isVisibleInWebsite && (
                    <div
                        key={plan.id}
                        className={`
              border rounded-xl p-6 flex flex-col relative transition-all duration-300
              hover:shadow-xl
              ${plan.popular
                                ? 'border-blue-500 shadow-md bg-blue-50/50'
                                : 'border-gray-200 hover:border-gray-300'}
            `}
                    >
                        {plan.popular && (
                            <div className="absolute top-0 right-0 m-3 px-4 py-1.5 bg-blue-500 text-white rounded-full text-xs font-semibold">
                                Most Popular
                            </div>
                        )}
                        <div className="mb-6">
                            <h3 className="text-2xl font-bold mb-2 text-gray-800">{plan.title}</h3>
                            <p className="text-gray-500 text-sm">{plan.description}</p>
                        </div>

                        <div className="mb-6">
                            <div className="text-4xl font-bold text-gray-900 mb-2">
                                ${plan.pricePerUser}
                                <span className="text-sm text-gray-500 ml-1.5">
                                    {plan.pricePerUser === 0 ? '/forever' : '/ month / user'}
                                </span>
                            </div>
                        </div>
                        {/* Subscribe button */}
                        {currentSubscription?.id !== plan.id && (
                            <Button
                                type={plan.popular ? 'primary' : 'default'}
                                className="mb-6 w-full py-2.5"
                                onClick={() => handleChoosePlan(plan.id)}
                                loading={loadingPriceId === plan.id}
                            >
                                Choose plan
                            </Button>
                        )}

                        {/* Unsubscribe button */}
                        {currentSubscription?.id === plan.id && (
                            <Button
                                type="dashed"
                                className="mb-6 w-full py-2.5 bg-red-100"
                                onClick={showConfirmModal}
                                loading={loadingPriceId === plan.id}
                            >
                                Unsubscribe
                            </Button>
                        )}


                        <div className="flex-grow">
                            <div className="space-y-3">
                                {plan.features.map((feature, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center"
                                    >
                                        <CheckCircleOutlined className="text-green-500 mr-3 text-lg" />
                                        <span className="text-gray-700 text-sm">
                                            {feature}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Confirmation modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '22px' }} />
                        <span>Confirm Unsubscription</span>
                    </div>
                }
                open={isModalVisible}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        danger
                        onClick={confirmUnsubscribe}
                    >
                        Yes, Unsubscribe
                    </Button>,
                ]}
            >
                <div style={{ padding: '12px 0' }}>
                    <Title level={5}>Are you sure you want to unsubscribe?</Title>
                    <Paragraph>
                        You (and your team) will immediately lose access to Trakhire.
                    </Paragraph>
                    <Paragraph>Do you wish to continue?</Paragraph>

                </div>
            </Modal>
        </Card>
    );
};

export default PricingSection;